<template>
  <div>
    <v-card outlined class="elevation-0">
      <v-card-title>{{ $t('autoHedge.riskSnapshot.title') }}</v-card-title>
      <v-card-text style="line-height: 1.4rem">
        <div class="d-flex align-center justify-space-between">
          <div>
            <p class="font-weight-bold">{{ $t('autoHedge.riskSnapshot.netExposure') }}</p>
            <h2
              class="metric-value mb-2"
              :class="{
                'pnl-negative' : currentExposureUSD < 0,
                'pnl-positive': currentExposureUSD >= 0,
                'paused-title': coreStore.getPauseDealOrderFlow
              }"
            >
              {{ formattedExposure }}
            </h2>
          </div>
          <div class="d-flex" style="gap:.4rem">
            <base-button  @click="toggleCurrency('USD')" :isActive="currentCurrency === 'USD'" class=" text-center" 
              >USD</base-button
            >
            <base-button @click="toggleCurrency('BRL')" :isActive="currentCurrency === 'BRL'" class=" text-center">BRL</base-button>
          </div>
        </div>
        <div class="exposure-chart">
          <div
            v-for="(exp, idx) in exposureHistory"
            :key="idx"
            class="exposure-bar"
            :class="{
              positive: exp > 0,
              negative: exp < 0,
              current: idx === exposureHistory.length - 1,
            }"
            :style="{ height: barHeight(exp) + '%' }"
            :title="tooltipText(exp, idx)"
          ></div>
        </div>
        <div>
          <h5>{{ $t('autoHedge.riskSnapshot.averageDealPrice') }}</h5>
          <div class="d-flex justify-space-between">
            <div>
              <h5>{{ $t('autoHedge.riskSnapshot.bid') }}</h5>
              <h3 class="font-weight-bold" style="color: #ff4444">{{ averageDealPriceBid }}</h3>
            </div>

            <div>
              <h5>{{ $t('autoHedge.riskSnapshot.ask') }}</h5>
              <h3 class="font-weight-bold" style="color: #00ff88">{{ averageDealPriceAsk }}</h3>
            </div>
          </div>
          <h5>{{ $t('autoHedge.riskSnapshot.currentMarketPrice') }}</h5>
          <div class="d-flex justify-space-between">
            <div>
              <h5>{{ $t('autoHedge.riskSnapshot.bid') }}</h5>
              <h3 class="font-weight-bold" style="color: #ff4444">{{ currentMarketPriceBid }}</h3>
            </div>

            <div>
              <h5>{{ $t('autoHedge.riskSnapshot.ask') }}</h5>
              <h3 class="font-weight-bold" style="color: #00ff88">{{ currentMarketPriceAsk }}</h3>
            </div>
          </div>
        </div>
        <div class="d-flex pt-3 justify-space-between">
          <h5>{{ $t('autoHedge.riskSnapshot.unrealizedPnl') }}</h5>
          <div class="d-flex " style="gap:.4rem">
            <base-button class=" text-center" :isActive="true"
              >{{ $t('autoHedge.riskSnapshot.mid') }}</base-button
            >
            <base-button class="text-center">{{ $t('autoHedge.riskSnapshot.exec') }}</base-button>
          </div>
        </div>
        <h2
          class="mb-2"
          :style="{ color: unrealizedPnL.sign === '+' ? '#00ff88' : '#ff4444' }"
        >
          {{ unrealizedPnL.sign }}{{ unrealizedPnL.pnl }}K
        </h2>
        <p style="color:#ff4444 ">
          {{ $t('autoHedge.riskSnapshot.diff') }} <i>{{ pipDiff }}</i>
        </p>
        <OffsetModal
        isOffset="RiskSnapShot"
          v-model="showModal"
          @confirm-event="executeOffset"
          :currentExposureUSD="currentExposureUSD"
        />
        <base-button
          block
          tabindex
          @click="offsetNow"
          @keydown="handleKeyDown"
          style="width: 100%; border: 1px solid #ffb00f; background: none"
          >{{ $t('autoHedge.riskSnapshot.offsetNow') }}
          <kbd
            style="
              font-size: 0.8rem;
              background: rgba(0, 0, 0, 0.2);
              padding: 2px 4px;
              border-radius: 2px;
            "
            >Shift+O</kbd
          ></base-button
        >
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { useCoreStore } from "@/store/coreStore";
import OffsetModal from "../../modal/OffsetModal.vue";
import { mapStores } from "pinia";

export default {
  components: { OffsetModal },
  data() {
    return {
      showModal: false,
      exposureHistory: [],
      currentExposureUSD: 2.4,
      currentCurrency:"USD",
    };
  },
  props: [
    "currentMarketPriceAsk",
    "currentMarketPriceBid",
    "averageDealPriceBid",
    "averageDealPriceAsk",
    "currentMarketPrice",
    "averageDealPrice",
  ],
  beforeUnmount() {
      window.removeEventListener("keydown", this.handleKeyDown)
  },
  beforeMount(){ this.exposureHistory = new Array(60).fill(0) },
  mounted() {
      
      window.addEventListener("keydown", this.handleKeyDown),
    setInterval(() => {
      if(!this.coreStore.getPauseDealOrderFlow)
      {
        this.currentExposureUSD += (Math.random() - 0.5) * 5;
        this.exposureHistory.unshift(this.currentExposureUSD);

      if (this.exposureHistory.length > 60) {
        this.exposureHistory.pop();
      }
      }
    }, 50000);
  },
  computed: {
    ...mapStores(useCoreStore),
    unrealizedPnL() {
      const diff = this.currentMarketPrice - this.averageDealPrice;
      const notional = this.currentExposureUSD * 1_000_000;
      const pnlUSD = notional * diff;

      const pnlK = pnlUSD / 1000;

      const sign = pnlK >= 0 ? "+" : "-";
      return { sign: sign, pnl: Math.abs(pnlK).toFixed(1) };
    },
    pipDiff() {
      const diff = (this.currentMarketPrice - this.averageDealPrice) * 10000;
      return `${diff >= 0 ? "+" : "-"}${diff.toFixed(
        1
      )} pips (${this.averageDealPrice.toFixed(
        4
      )} -> ${this.currentMarketPrice.toFixed(4)})`;
    },
    formattedExposure() {

      if(this.currentCurrency === "USD")
    {
      const sign = this.currentExposureUSD >= 0 ? "+" : "-";
      return `${sign}${Math.abs(this.currentExposureUSD).toFixed(2)}M`;
    }
    else if (this.currentCurrency === "BRL")
    {
      const brlValue = this.currentExposureUSD * 5.04 //TODO change conversion
      const sign = brlValue >= 0 ? "+" : "-";
      return `${sign}R${Math.abs(brlValue).toFixed(2)}M`;
    }

    },
  },
  methods: {
    handleKeyDown(e) {
      if (e.shiftKey && e.key.toLowerCase() === "o") {
        e.preventDefault();
        this.offsetNow();
      }
    },

    toggleCurrency(value)
    {
      this.currentCurrency = value;
    },
    offsetNow() {
      this.coreStore.savePauseDealOrderFlow(true)
      this.showModal = true;
    },
    executeOffset(input) {
      // Calculate suggested hedge size (opposite of current exposure)
      const suggestedSize = Math.abs(this.currentExposureUSD);
      const size = input;
      if (size && !isNaN(size)) {
        const hedgeAmount = parseFloat(size);

        // Simulate offset execution
        const dealPrice = (5.0421 + (Math.random() - 0.5) * 0.002).toFixed(4);
        const offsetPrice = (5.0421 + (Math.random() - 0.5) * 0.004).toFixed(4);
        const spread =
          (parseFloat(offsetPrice) - parseFloat(dealPrice)) * 10000; // in bps
        const spreadPct = (spread / 100).toFixed(2);
        const realizedPnL = `${spread >= 0 ? "+" : ""}${spreadPct}% / ${
          spread >= 0 ? "+" : ""
        }${spread.toFixed(1)}bps`;

        const finalRealizedPnL= {
          label:`${spread >= 0 ? "+" : ""}`,
          bps: spread.toFixed(1),
          pct:parseFloat(spreadPct)
        }

        // Update net exposure (offset reduces exposure toward zero)
        if (this.currentExposureUSD > 0) {
          this.currentExposureUSD = Math.max(
            0,
            this.currentExposureUSD - hedgeAmount
          );
        } else {
          this.currentExposureUSD = Math.min(
            0,
            this.currentExposureUSD + hedgeAmount
          );
        }

        this.$emit("new-log-entry", {
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          action: "MANUAL",
          hedgeSize: `${hedgeAmount.toFixed(1)}M`,
          dealPrice,
          offsetPrice,
          pnl: finalRealizedPnL,
        });
        // Show confirmation
        const cost = Math.abs(spread * hedgeAmount * 10); // Approximate cost in USD
        // alert(`✅ Offset executed successfully!\n\nHedge Size: ${hedgeAmount.toFixed(1)}M\nDeal Price: ${dealPrice}\nOffset Price: ${offsetPrice}\nRealized P&L: ${realizedPnL}\nEstimated Cost: ${cost.toFixed(0)}\n\nNew Net Exposure: ${currentExposureUSD >= 0 ? '+' : ''}${currentExposureUSD.toFixed(1)}M`);
        this.coreStore.savePauseDealOrderFlow(false)
      }
    },

    barHeight(exposure) {
      const allValues = [...this.exposureHistory, this.currentExposureUSD];
      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
      const range = maxValue - minValue;
      const normalized =
        range > 0 ? Math.abs(exposure - minValue) / range : 0.5;
      return Math.max(normalized * 100, 10);
    },
    tooltipText(exposure, index) {
      const sign = exposure >= 0 ? "+" : "";
      return `${sign}${exposure.toFixed(1)}M USD (${60 - index} min ago)`;
    },
  },
};
</script>
<style scoped>

.exposure-chart {
  display: flex;
  align-items: flex-end;
  height: 50px; /* chart height */
  gap: 4px;
  background: black;
  padding: 4px;
}
.paused-title{
  color:#ffbf00!important;
}
.exposure-bar {
  flex: 1;
  background: gray;
}
.exposure-bar.positive {
  background: #00ff88;
}
.exposure-bar.negative {
  background: #ff4444;
}
.exposure-bar.current {
  border: 2px solid gold;
}

.pnl-positive {
  color: #00ff88;
}
.pnl-negative {
  color: #ff4444;
}
</style>
